import { ClientFormValues } from '../components/clients-form'
import Contractor, { ApprovalStatus } from '../../../types/api/contractor'
import { ConvertRelationFields } from '../../../types/strapi/strapi'
import { normalizeClientFormValues, NormalizedContactInput } from './client-form-helpers'

interface CreateContactCallbacks {
    createContact: (data: { data: NormalizedContactInput['data'] }) => Promise<{ data: { documentId: string } }>
}

interface SyncContactCallbacks {
    createContact: (data: { data: NormalizedContactInput['data'] }) => Promise<{ data: { documentId: string } }>
    updateContact: (params: { docId: string; body: { data: NormalizedContactInput['data'] } }) => Promise<any>
}

export const createContacts = async (
    values: ClientFormValues,
    callbacks: CreateContactCallbacks
): Promise<string[]> => {
    const { contacts } = normalizeClientFormValues(values)
    const contactDocIds: string[] = []

    for (const contact of contacts) {
        if (!contact.hasData) continue

        const response = await callbacks.createContact({ data: contact.data })
        const docId = response.data?.documentId

        if (docId) {
            contactDocIds.push(docId)
        }
    }

    return contactDocIds
}

export const syncContacts = async (
    values: ClientFormValues,
    callbacks: SyncContactCallbacks
): Promise<string[]> => {
    const { contacts } = normalizeClientFormValues(values)
    const contactDocIds: string[] = []

    for (const contact of contacts) {
        if (!contact.hasData) continue

        if (contact.documentId) {
            await callbacks.updateContact({
                docId: contact.documentId,
                body: { data: contact.data },
            })
            contactDocIds.push(contact.documentId)
        } else {
            const response = await callbacks.createContact({ data: contact.data })
            const docId = response.data?.documentId

            if (docId) {
                contactDocIds.push(docId)
            }
        }
    }

    return contactDocIds
}

interface BuildContractorDataParams {
    values: ClientFormValues
    clientTypeId: string
    contactDocIds: string[]
    existingContractor?: Contractor
}

const buildCuratorsPayload = (values: ClientFormValues) => {
    const { curators } = normalizeClientFormValues(values)

    return curators.filter(curator => curator.hasData).map(curator => curator.data)
}

export const buildCreateContractorData = (
    params: BuildContractorDataParams
): ConvertRelationFields<
    Omit<Contractor, keyof import('../../../types/strapi/strapi').StandardStrapiFields>
> => {
    const { values, clientTypeId, contactDocIds } = params
    const { base } = normalizeClientFormValues(values)

    const createData: ConvertRelationFields<
        Omit<Contractor, keyof import('../../../types/strapi/strapi').StandardStrapiFields>
    > = {
        name: base.name,
        type: { connect: [clientTypeId] },
        approvalStatus: (base.approvalStatus || 'draft') as ApprovalStatus,
        defermentInDays: base.defermentInDays ?? 0,
        curators: buildCuratorsPayload(values) as any,
    }

    if (base.legalAddress !== undefined) {
        createData.legalAddress = base.legalAddress
    }
    if (base.source !== undefined) {
        createData.source = base.source
    }
    if (base.exhibitionName !== undefined) {
        createData.exhibitionName = base.exhibitionName
    }
    if (base.comment !== undefined) {
        createData.comment = base.comment
    }
    if (contactDocIds.length > 0) {
        createData.contractor_contacts = { connect: contactDocIds } as any
    }

    return createData
}

export const buildUpdateContractorData = (
    params: BuildContractorDataParams
): Partial<ConvertRelationFields<Contractor>> => {
    const { values, clientTypeId, contactDocIds, existingContractor } = params
    const { base } = normalizeClientFormValues(values)

    const updateData: Partial<ConvertRelationFields<Contractor>> = {
        name: base.name,
        type: { set: [clientTypeId] },
        approvalStatus: (base.approvalStatus || existingContractor?.approvalStatus || 'draft') as ApprovalStatus,
        defermentInDays: base.defermentInDays ?? 0,
        curators: buildCuratorsPayload(values) as any,
    }

    if (base.legalAddress !== undefined) {
        updateData.legalAddress = base.legalAddress
    }
    if (base.source !== undefined) {
        updateData.source = base.source
    }
    if (base.exhibitionName !== undefined) {
        updateData.exhibitionName = base.exhibitionName
    }
    if (base.comment !== undefined) {
        updateData.comment = base.comment
    }

    updateData.contractor_contacts = { set: contactDocIds } as any

    return updateData
}
